import { AddIcon } from "@minket/brand/icons/add";
import { BroadcastsIcon } from "@minket/brand/icons/broadcasts";
import { Button } from "@minket/ui/components/button";
import { EmptyState } from "@minket/ui/components/empty-state";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import type { CreateBody } from "@/components/broadcasts/create-broadcast.ondemand";
import { BroadcastLedger } from "@/components/broadcasts/ledger";
import { UnavailableAction } from "@/components/page/action";
import { GhostTable } from "@/components/page/ghost";
import { Page } from "@/components/page/page";
import { Scoped } from "@/components/page/scope";
import { QueryView } from "@/components/page/states";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiKey, apiQuery, apiRequest } from "@/lib/query";
import { notify } from "@/lib/toast";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/broadcasts/")({
  component: Broadcasts,
});

const CreateBroadcast = lazy(() =>
  import("@/components/broadcasts/create-broadcast.ondemand").then((m) => ({
    default: m.CreateBroadcastDialog,
  })),
);

const columns = ["Status", "Broadcast", "Progress", "Time, UTC"] as const;
const PAGE = 50;
const will = "Each broadcast will be listed here with its audience, its progress and its time.";

function Broadcasts() {
  const { project } = useWorkspace();
  const projectId = project?.id ?? "";
  const [after, setAfter] = useState<string | undefined>();
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const list = useQuery({
    ...apiQuery(endpoints.broadcasts, { projectId }, { limit: PAGE, after }),
    enabled: project !== null,
  });
  const has = list.isSuccess && list.data.data.length > 0;
  const segments = useQuery({
    ...apiQuery(endpoints.segments, { projectId }, { limit: 100 }),
    enabled: has,
  });
  const topics = useQuery({ ...apiQuery(endpoints.topics, { projectId }), enabled: has });
  const create = useMutation({
    mutationFn: (body: CreateBody) =>
      apiRequest(endpoints.createBroadcast, { projectId }, { body }),
    onSuccess: (broadcast) => {
      void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.broadcasts, { projectId }) });
      notify("success", "Draft created.");
      setCreating(false);
      void navigate({ to: "/broadcasts/$id", params: { id: broadcast.id } });
    },
  });
  const canManage = useCan("broadcasts.manage");
  const names = new Map<string, string>([
    ...(segments.data?.data ?? []).map((s) => [s.id, s.name] as const),
    ...(topics.data?.data ?? []).map((t) => [t.id, t.name] as const),
  ]);

  const action =
    project && canManage ? (
      <Button
        onClick={() => {
          create.reset();
          setCreating(true);
        }}
      >
        <AddIcon size={20} className="size-5" />
        New broadcast
      </Button>
    ) : (
      <UnavailableAction
        label="New broadcast"
        icon={AddIcon}
        reason={project ? "Viewers can read broadcasts but not create them" : "Needs a project"}
      />
    );

  return (
    <Page title="Broadcasts" action={action}>
      <Scoped
        scope="project"
        will={will}
        icon={<BroadcastsIcon />}
        preview={(busy) => <GhostTable columns={columns} label="broadcasts" busy={busy} />}
      >
        {() => (
          <QueryView
            query={list}
            noun="broadcasts"
            preview={(busy) => <GhostTable columns={columns} label="broadcasts" busy={busy} />}
          >
            {(page) =>
              page.data.length === 0 && after === undefined ? (
                <EmptyState
                  data-state="empty"
                  icon={<BroadcastsIcon />}
                  title="No broadcasts yet"
                  description="Write one, choose its audience, send yourself a test, then send it now or at a time."
                  preview={<GhostTable columns={columns} label="broadcasts" />}
                />
              ) : (
                <div className="grid min-w-0 gap-3">
                  <BroadcastLedger rows={page.data} names={names} />
                  {page.has_more || after !== undefined ? (
                    <nav aria-label="Pages of broadcasts" className="flex justify-end gap-2">
                      {after !== undefined ? (
                        <Button variant="outline" size="sm" onClick={() => setAfter(undefined)}>
                          Newest
                        </Button>
                      ) : null}
                      {page.has_more ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAfter(page.data.at(-1)?.id)}
                        >
                          Older
                        </Button>
                      ) : null}
                    </nav>
                  ) : null}
                </div>
              )
            }
          </QueryView>
        )}
      </Scoped>
      {creating && project ? (
        <Suspense fallback={null}>
          <CreateBroadcast create={create} onClose={() => setCreating(false)} />
        </Suspense>
      ) : null}
    </Page>
  );
}
